import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useDancers } from '@/data/dancers';
import { useDanceCast, useAssignCast, useRemoveCast } from '@/data/productions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

interface CastModalProps {
  open: boolean;
  onClose: () => void;
  danceId: string | null;
  danceName?: string | undefined;
}

interface CastRow {
  id: string;
  dance_id: string;
  dancer_id: string;
  role: string | null;
  dancers: {
    id: string;
    first_name: string;
    last_name: string;
    preferred_name: string | null;
  } | null;
}

export function CastModal({ open, onClose, danceId, danceName }: CastModalProps) {
  const [search, setSearch] = useState('');
  const dancers = useDancers({ search: search || undefined, limit: 50 });
  const cast = useDanceCast(danceId ?? undefined);
  const assignCast = useAssignCast();
  const removeCast = useRemoveCast();

  const assignedIds = new Set(((cast.data ?? []) as CastRow[]).map((r) => r.dancer_id));
  const available = (dancers.data?.rows ?? []).filter((d) => !assignedIds.has(d.id));

  async function handleAssign(dancerId: string) {
    if (!danceId) return;
    try {
      await assignCast.mutateAsync({ dance_id: danceId, dancer_id: dancerId });
    } catch (err) {
      toast({
        title: "Couldn't assign",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function handleRemove(assignmentId: string) {
    if (!danceId) return;
    try {
      await removeCast.mutateAsync({ id: assignmentId, dance_id: danceId });
    } catch (err) {
      toast({
        title: "Couldn't remove",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-overlay)] bg-[var(--color-ink)]/30 backdrop-blur-sm" />
        <Dialog.Content
          className="surface-elevated fixed top-1/2 left-1/2 z-[var(--z-modal)] max-h-[80vh] w-[min(90vw,40rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto p-6"
          aria-describedby={undefined}
        >
          <Dialog.Title className="font-[var(--font-display)] text-[var(--text-2xl)]">
            Cast for {danceName ?? 'this dance'}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-[var(--color-ink-muted)]">
            Add or remove dancers. Conflicts will appear in the lineup panel.
          </Dialog.Description>

          <section aria-labelledby="cast-current" className="mt-6">
            <h3
              id="cast-current"
              className="font-medium tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase"
            >
              Cast
            </h3>
            {(cast.data ?? []).length === 0 ? (
              <p className="mt-2 text-[var(--color-ink-muted)]">No dancers cast yet.</p>
            ) : (
              <ul className="mt-2 grid gap-1.5">
                {(cast.data ?? []).map((r) => {
                  const row = r as CastRow;
                  const d = row.dancers;
                  return (
                    <li
                      key={row.id}
                      className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--color-border)] p-2"
                    >
                      <span>
                        {d ? (d.preferred_name ?? d.first_name) : '???'} {d ? d.last_name : ''}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => void handleRemove(row.id)}>
                        Remove
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="cast-add" className="mt-6">
            <h3
              id="cast-add"
              className="font-medium tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase"
            >
              Add dancer
            </h3>
            <Label htmlFor="cast-search" className="sr-only">
              Search dancers
            </Label>
            <Input
              id="cast-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
            />
            <ul className="mt-2 grid max-h-72 gap-1 overflow-y-auto">
              {available.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => void handleAssign(d.id)}
                    className="flex w-full items-center justify-between rounded-[var(--radius-md)] px-3 py-2 text-left hover:bg-[var(--color-surface-sunken)] focus-visible:outline-none"
                  >
                    <span>
                      {d.preferred_name ?? d.first_name} {d.last_name}
                    </span>
                    <span className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                      add →
                    </span>
                  </button>
                </li>
              ))}
              {available.length === 0 && search.length > 0 && (
                <li className="px-3 py-2 text-[var(--color-ink-muted)]">No matching dancers.</li>
              )}
            </ul>
          </section>

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <Button variant="secondary">Done</Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
